import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useActions } from 'hooks/useActions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { ReactComponent as DownloadSvg } from 'assets/svg/upload.svg';
import { ReactComponent as SearchSvg } from 'assets/svg/search.svg';
import { ReactComponent as ThreeDotsSvg } from 'assets/svg/threedots.svg';
import { ReactComponent as UserSvg } from 'assets/svg/user.svg';
import { ReactComponent as LogoutSvg } from 'assets/svg/logout.svg';
import { ReactComponent as DownArrowSvg } from 'assets/svg/arrowDown.svg';
import Divider from 'components/UI/Divider';
import Table from 'components/UI/Table';
import Calendar from 'components/UI/Calendar';
import PaginationComponent from 'components/UI/Pagination';
import PageSizer from 'components/UI/PageSizer/PageSizer';
import UserAvatar from 'components/UI/UserAvatar';
import { tableHeader } from 'helpers';
import FacebookIcon from 'assets/svg/facebook.svg';
import tableContainer from './TableContainer';
import CsvButton from 'components/CsvButton/CsvButton';
import styles from './Users.module.scss';
import { Button, message } from 'antd';
import { setAllToFreeTrialQuery } from 'api/users'
import { Copy } from 'lucide-react';

const cancellationFeedbackLabels: Record<string, string> = {
    customer_service: 'Customer service was less than expected',
    low_quality: 'Quality was less than expected',
    missing_features: 'Missing features',
    other: 'Other',
    switched_service: 'Switched to another service',
    too_complex: 'Too complex',
    too_expensive: 'Too expensive',
    unused: 'Not used enough',
};

const cancellationReasonLabels: Record<string, string> = {
    cancellation_requested: 'Cancellation requested',
    payment_disputed: 'Payment disputed',
    payment_failed: 'Payment failed',
};

const Users = () => {
    const { getUsersData } = useActions();
    const { usersData, dist } = useTypedSelector((state) => state.users);
    const navigate = useNavigate();
    useEffect(() => {
        // Don't clobber an existing query string (e.g. ?filter=paid) on mount.
        if (!window.location.search) {
            navigate('/users?sort=datedesc');
        }
    }, []);
    const {
        onChangeHandler,
        endDate,
        selectHandler,
        logout,
        defaultPageSize,
        onShowSizeChange,
        onChangePagination,
        searchParams,
        searchUserHandler,
        sort,
        dd,
        page,
        limit,
        startDate,
        filter,
    } = tableContainer({ getData: getUsersData, locationParams: 'users' });

    const tabHref = (forFilter: 'paid' | 'cancelled' | null) => {
        const parts = [
            searchParams ? `search=${encodeURIComponent(searchParams)}` : '',
            sort ? `sort=${sort}` : '',
            limit ? `limit=${limit}` : '',
            startDate ? `dateFrom=${startDate}` : '',
            endDate ? `dateTo=${endDate}` : '',
            forFilter ? `filter=${forFilter}` : '',
        ].filter(Boolean).join('&');
        return `/users${parts ? `?${parts}` : ''}`;
    };

    const onClickEnableFreeTrial = () => {
        setAllToFreeTrialQuery().then((data) => {
               message.success(data.message)
            });
    };

    const copyEmail = async (email: string) => {
        try {
            await navigator.clipboard.writeText(email);
            message.success('Email copied');
        } catch {
            message.error('Could not copy email');
        }
    };

    const isCancelledTab = filter === 'cancelled';

    const cancellationStatus = (effectiveAt?: string, isActive?: boolean) => {
        if (!effectiveAt || !isActive || !dayjs(effectiveAt).isAfter(dayjs())) return 'Ended';
        const daysRemaining = Math.max(0, dayjs(effectiveAt).startOf('day').diff(dayjs().startOf('day'), 'day'));
        return daysRemaining === 0 ? 'Ends today' : `Ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
    };

    const cancellationReason = (feedback?: string, reason?: string) => {
        if (feedback) return cancellationFeedbackLabels[feedback] || feedback.replaceAll('_', ' ');
        if (reason) return cancellationReasonLabels[reason] || reason.replaceAll('_', ' ');
        return 'Not provided';
    };

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <h1 className={styles.mainTitle}>User information</h1>
                <p className={styles.mobileTitleSubtext}>View your all users that registered.</p>
                <div className={styles.buttonsContainer}>
                    <div className={styles.downloadBtn}>
                        <CsvButton data={usersData}>
                            {' '}
                            <DownloadSvg /> Download CSV
                        </CsvButton>
                    </div>
                </div>

            </div>
            <p className={styles.titleSubtext}>View your all users that registered.</p>
            <div className={styles.navigationBox}>
                <nav className={styles.navContainer}>
                    <ul>
                        <li className={filter ? styles.inactiveTab : ''}>
                            <Link to={tabHref(null)}>All users</Link>
                        </li>
                        <li className={filter === 'paid' ? '' : styles.inactiveTab}>
                            <Link to={tabHref('paid')}>Paid users</Link>
                        </li>
                        <li className={filter === 'cancelled' ? '' : styles.inactiveTab}>
                            <Link to={tabHref('cancelled')}>Cancelled</Link>
                        </li>
                    </ul>
                </nav>
                <div className={styles.logoutContainer} onClick={logout}>
                    <LogoutSvg className={styles.logoutSvg} />
                    <span className={styles.text}>Log Out</span>
                </div>
            </div>

            <div className={styles.divider}>
                <Divider />
            </div>
            <div className={styles.dataCalendar}>
                <div className={styles.searchInputWrapper}>
                    <input
                        type="text"
                        defaultValue={searchParams || ''}
                        placeholder="Search for users"
                        onChange={(e) => {
                            searchUserHandler(`${sort ? `sort=${sort}&` : ''}${dd}`, e);
                        }}
                    />
                    <div className={styles.searchIcon}>
                        <SearchSvg />
                    </div>
                </div>
                <div className={styles.logoutContainer}>
                    {/* <Button
                        danger
                        onClick={onClickEnableFreeTrial}
                    >Enable Free Trial
                    </Button> */}
                </div>
                <div className={styles.calendarWrapper}>
                    <div className={styles.dataPickers}>
                        <Calendar isMaxDate startDate={startDate} onChange={onChangeHandler} endDate={endDate} />
                    </div>
                </div>
            </div>
            <div className={styles.tableHeader}>
                <h2 className={styles.title}>
                    {isCancelledTab ? 'Cancellations' : filter === 'paid' ? 'Paid Users' : 'All Users'} - total {dist}
                </h2>
                <ThreeDotsSvg />
            </div>
            <Table>
                <thead>
                    {isCancelledTab ? (
                        <tr>
                            <th>User</th>
                            <th>Subscription plan</th>
                            <th>
                                <Link to={`/users?${searchParams ? `search=${encodeURIComponent(searchParams)}&` : ''}sort=${sort === 'dateasc' ? 'datedesc' : 'dateasc'}&${dd}`}>
                                    Cancelled on <DownArrowSvg />
                                </Link>
                            </th>
                            <th>Access ends</th>
                            <th>Status</th>
                            <th>Reason</th>
                        </tr>
                    ) : (
                    <tr>
                        {tableHeader.map((item) => {
                            return (
                                <th key={item.id}>
                                    <div className={styles.columnHeader}>
                                        {!(item.title === 'Created profile' || item.title === 'Digest numbers') &&
                                            item.title}
                                        {item.title === 'Created profile' && (
                                            <Link
                                                to={`/users?${searchParams ? `search=${encodeURIComponent(searchParams)}&` : ''}sort=${
                                                    sort === 'dateasc' ? 'datedesc' : 'dateasc'
                                                }&${dd}`}
                                            >
                                                {item.title}
                                                {sort !== 'numasc' && sort !== 'numdesc' && sort && (
                                                    <span
                                                        className={`${styles.svg} ${
                                                            sort === 'dateasc' ? styles.arrowSvg : ''
                                                        }`}
                                                    >
                                                        <DownArrowSvg />
                                                    </span>
                                                )}
                                            </Link>
                                        )}
                                        {item.title === 'Digest numbers' && (
                                            <Link
                                                to={`/users?${searchParams ? `search=${encodeURIComponent(searchParams)}&` : ''}sort=${
                                                    sort === 'numasc' ? 'numdesc' : 'numasc'
                                                }&${dd}`}
                                            >
                                                {item.title}
                                                {sort !== 'dateasc' && sort !== 'datedesc' && sort && (
                                                    <span
                                                        className={`${styles.svg} ${
                                                            sort === 'numasc' ? styles.arrowSvg : ''
                                                        }`}
                                                    >
                                                        <DownArrowSvg />
                                                    </span>
                                                )}
                                            </Link>
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                    )}
                </thead>
                <tbody>
                    {isCancelledTab ? usersData.map((user) => (
                        <tr key={user.subscriptionRecordId || user.stripeSubscriptionId || user.userId}>
                            <td>
                                <div className={styles.userInfoColumn}>
                                    <div className={styles.image}>
                                        <UserAvatar
                                            src={user.profileImg}
                                            name={user.fullName}
                                            email={user.email}
                                        />
                                    </div>
                                    <div className={styles.userCredentials}>
                                        <Link to={user.userId} className={styles.name}>{user.fullName}</Link>
                                        <div className={styles.emailRow}>
                                            <Link to={user.userId} className={styles.email}>{user.email}</Link>
                                            <button type="button" className={styles.copyEmailButton} onClick={() => copyEmail(user.email)} aria-label={`Copy ${user.email}`} title="Copy email">
                                                <Copy size={12} aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={styles.dateColumn}>{user.subscriptionPlan || 'Unknown'}</td>
                            <td className={styles.dateColumn}>{user.cancellationRequestedAt ? dayjs(user.cancellationRequestedAt).format('MMM DD YYYY') : 'Unknown'}</td>
                            <td className={styles.dateColumn}>{user.cancellationEffectiveAt ? dayjs(user.cancellationEffectiveAt).format('MMM DD YYYY') : 'Unknown'}</td>
                            <td>
                                <span className={user.isActive ? styles.pendingCancellation : styles.endedCancellation}>
                                    {cancellationStatus(user.cancellationEffectiveAt, user.isActive)}
                                </span>
                            </td>
                            <td className={styles.reasonColumn}>
                                <span>{cancellationReason(user.cancellationFeedback, user.cancellationReason)}</span>
                                {user.cancellationComment && <small>{user.cancellationComment}</small>}
                            </td>
                        </tr>
                    )) : usersData.map((user) => {
                        const sources = user.sources || [];
                        let remainingUsersCount;
                        if (sources.length > 5) {
                            remainingUsersCount = sources.length - 5;
                        }
                        return (
                            <tr key={user.userId}>
                                <td>
                                    <div className={styles.userInfoColumn}>
                                        <div className={styles.image}>
                                            <UserAvatar
                                                src={user.profileImg}
                                                name={user.fullName}
                                                email={user.email}
                                            />
                                        </div>
                                        <div className={styles.userCredentials}>
                                            <Link to={user.userId} className={styles.name}>{user.fullName}</Link>
                                            <div className={styles.emailRow}>
                                                <Link to={user.userId} className={styles.email}>{user.email}</Link>
                                                <button
                                                    type="button"
                                                    className={styles.copyEmailButton}
                                                    onClick={() => copyEmail(user.email)}
                                                    aria-label={`Copy ${user.email}`}
                                                    title="Copy email"
                                                >
                                                    <Copy size={12} aria-hidden="true" />
                                                </button>
                                            </div>
                                            {user.signupIp && (
                                                <Link to={user.userId} className={styles.signupIp}>{user.signupIp}</Link>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className={styles.digestNumberColumn}>{user.digestNumbers}</td>
                                <td>
                                    <div className={styles.sourcesColumn}>
                                        <span className={styles.sourceNumber}>{sources.length}</span>
                                        <div className={styles.imageContainer}>
                                            {sources
                                                .slice(0, 5).map((img: string, index: number) => {
                                                    return (
                                                        <div key={index} className={styles.image}>
                                                            <img
                                                                src={img.includes('/sources/FACEBOOK.svg') ? FacebookIcon : img}
                                                                alt="source icon"
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            {sources.length > 5 && (
                                                <div className={styles.countContainer}>
                                                    <span className={styles.count}>+{remainingUsersCount}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className={styles.dateColumn}>{dayjs(user.createdAt).format('MMM DD YYYY')}</td>
                                <td className={styles.dateColumn}>{user.method}</td>
                                <td className={styles.dateColumn}>{user.subscriptionPlan}</td>
                                <td className={styles.dateColumn}>{user.userMailNumbers}</td>
                                <td className={styles.dateColumn}>{user.userDigestTemplateUsage}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            <div className={styles.tableFooter}>
                <div className={styles.pageSizerContainer}>
                    <UserSvg />
                    <span className={styles.text}> Accounts per page</span>
                    <div className={styles.sizerWrapper}>
                        <PageSizer currentItem={limit || '20'} onClick={selectHandler} array={['10', '20', '50']} />
                    </div>
                </div>
                <PaginationComponent
                    defaultPageSize={defaultPageSize}
                    total={dist}
                    defaultCurrent={page ? Number(page) : 1}
                    onChange={onChangePagination}
                    onShowSizeChange={onShowSizeChange}
                />
            </div>
        </div>
    );
};

export default Users;
